/**
 * Commercial base records — clients, contacts, projects, locations — and the
 * numbering machinery every numbered record type will draw on.
 *
 * Status NAMES are configurable per laboratory; their MEANINGS are fixed, and
 * these constants are the internal identifiers. Display labels come from the
 * i18n catalogues, never from these values.
 */
// ---------------------------------------------------------------------------
// Client status
// ---------------------------------------------------------------------------
/**
 * A client is either dealt with or not dealt with. There is no third state and
 * no deletion.
 *
 * SUSPENDED means "no NEW dealings". It says nothing about what already
 * happened: every project, request, report and invoice already on the file
 * stays exactly as it was, remains readable, and remains attributable
 * (requirements p.8). Suspension is a forward-looking decision, never a way to
 * make history disappear.
 */
export const CLIENT_STATUS = {
    ACTIVE: 'active',
    SUSPENDED: 'suspended',
};
export const ALL_CLIENT_STATUSES = Object.freeze(Object.values(CLIENT_STATUS));
/** May a NEW dealing be opened against this client? */
export function clientAcceptsNewDealings(status) {
    return status === CLIENT_STATUS.ACTIVE;
}
/**
 * What kind of party the client is.
 *
 * Present because the identifying documents differ — a company has a
 * commercial registration, an individual does not — not because the laboratory
 * treats them differently. Nothing in the workflow branches on this.
 */
export const CLIENT_TYPE = {
    COMPANY: 'company',
    GOVERNMENT: 'government',
    INDIVIDUAL: 'individual',
    OTHER: 'other',
};
export const ALL_CLIENT_TYPES = Object.freeze(Object.values(CLIENT_TYPE));
// ---------------------------------------------------------------------------
// Contact portal eligibility
// ---------------------------------------------------------------------------
/**
 * How much of the client file a contact sees in the client portal.
 *
 * Recorded on the contact since Phase 4 as a flag only; Phase 11 builds the
 * portal that reads it (D146, D148). Setting it still creates no account — a
 * portal account is created separately by a holder of
 * `portal_accounts.manage` — and a contact enabled for the portal without a
 * visibility receives the laboratory's `portal_default_visibility`, applied by
 * the backend. `reports_and_invoices` (financial visibility) is always an
 * explicit grant.
 */
export const PORTAL_VISIBILITY = {
    /** The default. No portal access is intended for this contact. */
    NONE: 'none',
    /** Issued reports for this client only. */
    REPORTS: 'reports',
    /** Issued reports and the client's own invoices and statements. */
    REPORTS_AND_INVOICES: 'reports_and_invoices',
};
export const ALL_PORTAL_VISIBILITIES = Object.freeze(Object.values(PORTAL_VISIBILITY));
// ---------------------------------------------------------------------------
// Project status
// ---------------------------------------------------------------------------
/**
 * A project's commercial state.
 *
 * `COMPLETED` and `CANCELLED` are both terminal for NEW work and neither
 * removes anything: a cancelled project keeps its record, its locations and
 * everything ever done under it.
 */
export const PROJECT_STATUS = {
    ACTIVE: 'active',
    /** Paused for a recorded reason. Not a cancellation — work resumes. */
    ON_HOLD: 'on_hold',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
};
export const ALL_PROJECT_STATUSES = Object.freeze(Object.values(PROJECT_STATUS));
/** May a NEW dealing be opened against this project? */
export function projectAcceptsNewWork(status) {
    return (status === PROJECT_STATUS.ACTIVE || status === PROJECT_STATUS.ON_HOLD);
}
// ---------------------------------------------------------------------------
// Numbering
// ---------------------------------------------------------------------------
/**
 * Every record type that receives a generated, human-quoted number.
 *
 * The full set exists from Phase 4 even though only `CLIENT` is consumed here.
 * The point is that the phase which first issues a Job Number adds a ROW, not
 * a mechanism: the rule, the sequence, the reset policy and the concurrency
 * guarantees are already built and already tested.
 */
export const NUMBERED_RECORD_TYPE = {
    CLIENT: 'client',
    WORK_ORDER: 'work_order',
    SAMPLE: 'sample',
    QUOTATION: 'quotation',
    REPORT: 'report',
    INVOICE: 'invoice',
    /** Phase 12 (D164): complaints, observations and non-conformities. */
    QUALITY_RECORD: 'quality_record',
    /** UX follow-up (D193): one numbered handover of several samples. */
    SAMPLE_RECEIPT: 'sample_receipt',
};
export const ALL_NUMBERED_RECORD_TYPES = Object.freeze(Object.values(NUMBERED_RECORD_TYPE));
/**
 * When the sequence returns to 1.
 *
 * A reset is only safe when the format carries the matching period token —
 * a yearly reset with no year in the format would re-issue last year's
 * numbers. That pairing is enforced by the validator, by the API and by a
 * database CHECK, because "numbers are never reused" (CLAUDE.md section 8) is
 * not a promise worth resting on one layer.
 */
export const NUMBERING_RESET_POLICY = {
    NEVER: 'never',
    YEARLY: 'yearly',
    MONTHLY: 'monthly',
};
export const ALL_NUMBERING_RESET_POLICIES = Object.freeze(Object.values(NUMBERING_RESET_POLICY));
/**
 * The default numbering format for each record type, applied when a laboratory
 * is CREATED.
 *
 * This is a default for a new organisation, not a hardcoded organisational
 * value: it lives in exactly one place — the backend — and every laboratory
 * edits its own copy afterwards through the settings interface. The frontend
 * must never restate these (CLAUDE.md section 6.1).
 *
 * Source: PLAN.md Q3.
 */
export const DEFAULT_NUMBERING_FORMATS = Object.freeze({
    [NUMBERED_RECORD_TYPE.CLIENT]: 'CL-{YYYY}-{00000}',
    [NUMBERED_RECORD_TYPE.WORK_ORDER]: 'JOB-{YYYY}-{00000}',
    [NUMBERED_RECORD_TYPE.SAMPLE]: 'SMP-{YYYY}-{000000}',
    [NUMBERED_RECORD_TYPE.QUOTATION]: 'QT-{YYYY}-{00000}',
    [NUMBERED_RECORD_TYPE.REPORT]: 'RPT-{YYYY}-{00000}',
    [NUMBERED_RECORD_TYPE.INVOICE]: 'INV-{YYYY}-{00000}',
    [NUMBERED_RECORD_TYPE.QUALITY_RECORD]: 'QA-{YYYY}-{00000}',
    [NUMBERED_RECORD_TYPE.SAMPLE_RECEIPT]: 'SR-{YYYY}-{00000}',
});
/** The default reset policy for a new laboratory: yearly, per PLAN.md Q3. */
export const DEFAULT_NUMBERING_RESET_POLICY = NUMBERING_RESET_POLICY.YEARLY;
// ---------------------------------------------------------------------------
// Duplicate detection
// ---------------------------------------------------------------------------
/**
 * How confident the system is that a candidate is the same client.
 *
 * NONE of these blocks anything. The strongest available outcome is a warning
 * that must be acknowledged by a named person, because a reception desk that
 * cannot record a real client standing at the counter is a worse failure than
 * a duplicate somebody merges next week (PLAN.md, Phase 4 risk).
 */
export const DUPLICATE_CONFIDENCE = {
    /** An identifying number, address or phone matched exactly. */
    EXACT: 'exact',
    /** The names are the same once punctuation, case and Arabic orthographic
     *  variants are normalised away. */
    STRONG: 'strong',
    /** The names are similar. Shown, and acknowledgeable, but frequently a real
     *  distinct client — two branches of one group, for instance. */
    POSSIBLE: 'possible',
};
/**
 * Confidences that require an explicit, recorded acknowledgement before a new
 * client is created. `POSSIBLE` deliberately does not: warning about every
 * loosely similar name would train the desk to click through warnings, which
 * is how a real duplicate gets in.
 */
export const DUPLICATE_CONFIDENCES_REQUIRING_ACKNOWLEDGEMENT = Object.freeze([DUPLICATE_CONFIDENCE.EXACT, DUPLICATE_CONFIDENCE.STRONG]);
export function duplicateRequiresAcknowledgement(confidence) {
    return DUPLICATE_CONFIDENCES_REQUIRING_ACKNOWLEDGEMENT.includes(confidence);
}
/** Which field produced a match. Rendered as a translated explanation. */
export const DUPLICATE_MATCH_FIELD = {
    NAME: 'name',
    EMAIL: 'email',
    PHONE: 'phone',
    VAT_REGISTRATION_NUMBER: 'vat_registration_number',
    COMMERCIAL_REGISTRATION_NUMBER: 'commercial_registration_number',
};
// ---------------------------------------------------------------------------
// Client-file sections that later phases fill in
// ---------------------------------------------------------------------------
/**
 * Whether a section of the client file can be answered yet.
 *
 * `NOT_IMPLEMENTED` is the honest answer for work orders, reports and invoices
 * in Phase 4: those modules do not exist, so the system does not know the
 * count. Returning `0` would assert a fact — "this client has no invoices" —
 * that nothing has established (CLAUDE.md section 8: an empty field is never
 * zero).
 */
export const CLIENT_FILE_SECTION_STATE = {
    AVAILABLE: 'available',
    NOT_IMPLEMENTED: 'not_implemented',
};
/**
 * Name-similarity thresholds, on the 0-to-1 scale `nameSimilarity` returns.
 *
 * Calibrated against the cases in `text.test.ts` rather than chosen round:
 *
 *  - At or above `STRONG`, the two names differ only in spelling, legal form
 *    or orthography — `شركة الأمل` and `شركه الامل`, `Al Amal Co` and
 *    `Al Amal Company`. A person must acknowledge before a second record is
 *    created.
 *  - Between `POSSIBLE` and `STRONG` sit the pairs that are genuinely
 *    ambiguous — `Riyadh Soil Labs` and `Riyadh Steel Labs`, `Northern Gulf
 *    Group` and `Southern Gulf Group`. These are SHOWN, so the desk can see
 *    them, but they do not gate the save. Most are two real clients, and
 *    demanding an acknowledgement for each would teach the desk to click
 *    through every warning — which is precisely how a real duplicate gets in.
 *  - Below `POSSIBLE`, nothing is said.
 */
export const DUPLICATE_NAME_SIMILARITY = Object.freeze({
    STRONG: 0.92,
    POSSIBLE: 0.75,
});
//# sourceMappingURL=commercial.js.map